import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Row, Col, Statistic, Table, Divider, Spin } from "antd";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";
import {
  PlusOutlined,
  FileTextOutlined,
  TeamOutlined,
  BarChartOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import QuickActionCard from "./QuickActionCard";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Button } from "antd";
import DetailsModal from "./DetailsModal";
import useFeedbackService from "../../../../services/feedbackService";
import TipsCard from "./TippsCard";

// ChartJS registrieren
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

const DashboardHome = () => {
  const [recentFeedbacks, setRecentFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [standardMetrics, setStandardMetrics] = useState({
    allFB: 0,
    avgRating: 0,
    activeForms: 0,
    wkFB: 0,
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const { getHomeData } = useFeedbackService();
  const { language } = useLanguage();
  const text = dashboardContent[language].home;
  const navigate = useNavigate();

  // Mock-Daten für die Karten
  const metrics = [
    {
      title: text.metrics.allFeedback,
      value: standardMetrics.allFB,
      color: "#1890ff",
    },
    {
      title: text.metrics.avgRating,
      value: standardMetrics.avgRating
        ? standardMetrics.avgRating.toFixed(1)
        : 0,
      suffix: "/ 5",
      color: "#52c41a",
    },
    {
      title: text.metrics.activeForms,
      value: standardMetrics.activeForms,
      color: "#faad14",
    },
    {
      title: text.metrics.thisWeek,
      value: standardMetrics.wkFB,
      color: "#ff4d4f",
    },
  ];

  // Tabellen-Daten
  const tableColumns = [
    { title: text.table.columnForm, dataIndex: "category", key: "category" },
    { title: text.table.columnFrom, dataIndex: "from", key: "from" },
    { title: text.table.columnRating, dataIndex: "rating", key: "rating" },
    {
      title: text.table.columnAction,
      key: "action",
      render: (_, record) => (
        <button onClick={() => handleShow(record)}>
          {text.table.actionShow}
        </button>
      ),
    },
  ];

  const handleShow = (record) => {
    setSelectedFeedback(record);
    setModalVisible(true);
  };
  const tableData = recentFeedbacks.map((feedback, index) => {
    // Extrahiere sender-Infos kurz
    const first = feedback.sender?.firstname?.trim() || "";
    const last = feedback.sender?.lastname?.trim() || "";
    const mail = feedback.sender?.email?.trim() || "";

    const isAnon = first.toLowerCase() === "anonym";
    const fromNode = isAnon ? (
      "UNBEKANNT"
    ) : (
      <>
        {first} {last}
        <br />
        {mail}
      </>
    );

    return {
      key: index.toString(),
      category: feedback.form?.name || "Unbekanntes Formular",
      from: fromNode,
      rating:
        feedback.responses.find(
          (res) => res.questionId === "679f639effa430a7f0756e34"
        )?.answer + " / 5" || "N/A",
      responses: feedback.responses,
      form: feedback.form,
      images: feedback.images,
    };
  });

  useEffect(() => {
    const getHomeDataL = async () => {
      setLoading(true);
      try {
        const response = await getHomeData();
        console.log("API Response:", response);

        if (response) {
          setRecentFeedbacks(response.recentFeedbacks);
          setStandardMetrics({
            allFB: response.metrics.allFeedbackCount,
            avgRating: response.metrics.averageRating,
            wkFB: response.metrics.feedbacksThisWeek,
            activeForms: response.metrics.formsCount,
          });
        } else {
          setRecentFeedbacks([]);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der Feedback-Daten:", error);
      }
      setLoading(false);
    };

    getHomeDataL();
  }, []);

  const quickActions = text.quickActions.map((action, i) => ({
    ...action,
    icon: [
      <PlusOutlined />,
      <TeamOutlined />,
      <FileTextOutlined />,
      <BarChartOutlined />,
      <SettingOutlined />,
    ][i],
  }));

  return (
    <div className="p-4 bg-gray-100 min-h-screen relative w-full">
      {loading && (
        <>
          <Spin
            className="!absolute top-[10%] sm:top-[20%] left-[50%] translate-x-[-50%] z-10"
            size="large"
          />
          <div className="!absolute top-0 left-0 h-full w-full blur-lg z-10 bg-slate-100 opacity-50"></div>
        </>
      )}

      {/* Karten mit Metriken */}
      <Row gutter={[16, 16]}>
        {metrics.map((metric, index) => (
          <Col key={index} xs={24} sm={12} md={6}>
            <Card
              style={{ borderColor: metric.color }}
              className="shadow-lg"
              bordered={false}
            >
              <Statistic
                title={metric.title}
                value={metric.value}
                valueStyle={{ color: metric.color }}
                suffix={metric.suffix || null}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Divider />

      <Row gutter={[24, 16]} className="mt-4">
        <Col xs={24} md={10}>
          <TipsCard />
        </Col>
        <Col xs={24} md={14} className="space-y-4">
          <Card title={text.table.title} className="shadow-lg">
            <Table
              columns={tableColumns}
              className="overflow-x-scroll custom-scrollbar"
              dataSource={tableData || []}
              pagination={{ pageSize: 5 }}
              locale={{ emptyText: text.table.emptyText }}
            />
            <Row justify="start" style={{ marginTop: 16 }}>
              <Col>
                <Button
                  className="bg-orange-500 border-orange-500 hover:bg-orange-600 text-white"
                  onClick={() => navigate("/dashboard/allfeedback")}
                >
                  {text.table.allFeedbacks || "Alle Feedbacks"}
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* Quick Actions */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={24}>
          <Card title={text.quickActionsTitle} className="shadow-md">
            {quickActions.map((action, index) => (
              <QuickActionCard key={index} {...action} />
            ))}
          </Card>
        </Col>
      </Row>
      <DetailsModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        feedback={selectedFeedback}
      />
      <Divider />
    </div>
  );
};

export default DashboardHome;
